import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, User, Bot, Phone, Sparkles, ShoppingCart, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AIChatWidget = ({ onAddToCart }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! 👋 I'm your UPLANDS FOOD MARKET shopping assistant. I can help you find products, suggest meals, or connect you with our staff. How can I help you today?",
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [addingToCart, setAddingToCart] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId,
        }),
      });

      const data = await response.json();
      
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: data.response,
          suggestions: data.suggestions,
          products: data.products,
        }
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm having trouble connecting. Please try again or contact our staff directly.",
          suggestions: ['Contact Staff', 'Try Again'],
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/ai/contact-staff`, {
        method: 'POST',
        headers,
      });

      const data = await response.json();
      
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `${data.message}\n\n📧 ${data.contact_email}\n📞 ${data.contact_phone}`,
        }
      ]);
      
      toast.success('Staff has been notified!');
    } catch (error) {
      toast.error('Failed to contact staff. Please try again.');
    }
  };

  const handleAddToCart = async (product) => {
    setAddingToCart(product.id);
    try {
      if (onAddToCart) {
        await onAddToCart(product);
        toast.success(`${product.name} added to cart!`);
      } else {
        // Fallback: dispatch custom event
        const event = new CustomEvent('addToCartFromChat', { detail: product });
        window.dispatchEvent(event);
        toast.success(`${product.name} added to cart!`);
      }
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const quickActions = [
    { label: '🍲 Meals', message: 'Suggest some meal ideas with African ingredients' },
    { label: '🔥 Popular', message: 'What are the most popular products?' },
    { label: '🛒 Help', message: 'I need help finding products' },
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-40 bg-gradient-to-r from-orange-500 to-red-500 text-white p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-1.5"
        data-testid="ai-chat-toggle"
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-xs font-semibold pr-1">AI Help</span>
      </button>
    );
  }

  return (
    <div 
      className="fixed bottom-4 right-4 z-40 bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      style={{ width: '280px', height: '380px' }}
      data-testid="ai-chat-widget"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-xs">AI Assistant</h3>
            <p className="text-[10px] opacity-80">Online 24/7</p>
          </div>
        </div>
        <button 
          onClick={() => setIsOpen(false)}
          className="hover:bg-white/20 p-1 rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gray-50">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[90%] ${msg.role === 'user' ? 'order-1' : 'order-2'}`}>
              <div 
                className={`p-2 rounded-xl text-xs ${
                  msg.role === 'user' 
                    ? 'bg-orange-500 text-white rounded-br-sm' 
                    : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border'
                }`}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
              
              {/* Inline Product Cards */}
              {msg.products && msg.products.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  <p className="text-[10px] text-gray-500 font-medium">🛒 Products:</p>
                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                    {msg.products.map((product, pIdx) => (
                      <div 
                        key={pIdx}
                        className="flex-shrink-0 bg-white rounded-lg border shadow-sm p-1.5 w-24"
                        data-testid={`chat-product-${product.id}`}
                      >
                        {product.image && (
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-12 object-cover rounded mb-1"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <p className="text-[10px] font-medium text-gray-800 line-clamp-2 mb-0.5">{product.name}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-orange-600">${product.price?.toFixed(2)}</span>
                          <button
                            onClick={() => handleAddToCart(product)}
                            disabled={addingToCart === product.id}
                            className="bg-orange-500 hover:bg-orange-600 text-white p-0.5 rounded-full transition-colors disabled:opacity-50"
                            data-testid={`chat-add-to-cart-${product.id}`}
                          >
                            {addingToCart === product.id ? (
                              <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            ) : (
                              <Plus className="w-2.5 h-2.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Suggestions */}
              {msg.suggestions && msg.suggestions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {msg.suggestions.map((suggestion, sIdx) => (
                    <button
                      key={sIdx}
                      onClick={() => {
                        if (suggestion === 'Contact Staff') {
                          handleContactStaff();
                        } else {
                          sendMessage(suggestion);
                        }
                      }}
                      className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full hover:bg-orange-200 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border">
              <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {messages.length <= 2 && (
        <div className="px-3 py-2 border-t bg-white">
          <div className="flex gap-1 overflow-x-auto">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(action.message)}
                className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contact Staff Button */}
      <div className="px-2 py-1.5 border-t bg-white">
        <a
          href="tel:+16137159147"
          className="w-full text-[10px] bg-green-50 text-green-700 px-2 py-1.5 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
        >
          <Phone className="w-3 h-3" />
          +1 613 715 9147
        </a>
      </div>

      {/* Input */}
      <div className="p-2 border-t bg-white">
        <form 
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="flex gap-1.5"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 text-xs h-8"
            disabled={isLoading}
            data-testid="ai-chat-input"
          />
          <Button 
            type="submit" 
            size="sm"
            disabled={isLoading || !input.trim()}
            className="bg-orange-500 hover:bg-orange-600 h-8 w-8 p-0"
            data-testid="ai-chat-send"
          >
            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AIChatWidget;
